import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

const primaryColor = ({ theme }) => theme?.colors?.background?.primary || '#7543DC';
const primaryHover = ({ theme }) => {
    const hex = theme?.colors?.background?.primary || '#7543DC';
    if (!hex || hex.length < 7) return '#5f32c4';
    const h = hex.replace('#', '');
    const r = Math.max(0, parseInt(h.slice(0, 2), 16) - 15);
    const g = Math.max(0, parseInt(h.slice(2, 4), 16) - 15);
    const b = Math.max(0, parseInt(h.slice(4, 6), 16) - 15);
    return `rgb(${r},${g},${b})`;
};

const linkStyles = css`
    position: relative;
    display: inline-block;
    text-align: ${({ $block }) => ($block ? 'center' : 'inherit')};
    margin-top: ${({ $block }) => ($block ? '16px' : '0')};
    font-size: ${({ $markdownInline }) => ($markdownInline ? 'inherit' : '0.9375rem')};
    font-weight: ${({ $markdownInline }) => ($markdownInline ? 'inherit' : 600)};
    color: ${primaryColor};
    text-decoration: none;
    transition: color 0.25s ease;
    cursor: pointer;
    user-select: text;
    -webkit-user-select: text;

    &:hover {
        color: ${primaryHover};
    }
`;

const StyledRouterLink = styled(RouterLink)`
    ${linkStyles}
`;

const StyledAnchor = styled.a.attrs({ draggable: false })`
    ${linkStyles}
`;

/** Non-navigating inline text with the same primary + underline-on-hover look as {@link Link} (e.g. @mentions in markdown). */
const StyledInlineSpan = styled(motion.span)`
    ${linkStyles}
    cursor: default;
    user-select: text;
`;

/** Button that looks like Link (primary color, underline on hover). For actions like "Mark as done". */
const linkButtonStyles = css`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    font-size: 0.9375rem;
    font-weight: 600;
    color: ${primaryColor};
    text-decoration: none;
    cursor: pointer;
    font-family: var(--font-family-geist);
    transition: color 0.25s ease;

    &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 100%;
        height: 2px;
        background: currentColor;
        transform: scaleX(0);
        transform-origin: left;
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover:not(:disabled) {
        color: ${primaryHover};
    }

    &:hover:not(:disabled)::after {
        transform: scaleX(1);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const StyledLinkButton = styled.button`
    ${linkButtonStyles}
`;

const underlineVariants = {
    rest: { scaleX: 0 },
    hover: { scaleX: 1 },
};

const underlineTransition = { duration: 0.35, ease: [0.4, 0, 0.2, 1] };

const MotionWrap = styled(motion.span)`
    display: ${({ $block }) => ($block ? 'block' : 'inline-block')};
    user-select: text;
    -webkit-user-select: text;
`;

const Underline = styled(motion.span)`
    position: absolute;
    left: 0;
    /* Keep underline inside the line box so it won't be clipped by line-clamp overflow. */
    bottom: 0px;
    width: 100%;
    height: 2px;
    background: ${primaryHover};
    transform-origin: left;
    pointer-events: none;
`;

const linkVariants = { rest: {}, hover: {} };

/**
 * Same visual and hover underline as {@link Link}: real anchor when `href` is set; otherwise a span (mentions).
 * @param {boolean} [inheritMarkdownSize] - When true, `font-size` matches parent (e.g. Markdown body 13px/14px).
 */
export function InlinePrimaryLink({ href, children, target, rel, inheritMarkdownSize, ...rest }) {
    const interactive = typeof href === 'string' && href.trim() !== '' && href !== '#';

    if (interactive) {
        return (
            <MotionWrap $block={false}>
                <StyledAnchor
                    href={href}
                    $block={false}
                    $markdownInline={inheritMarkdownSize}
                    target={target}
                    rel={rel}
                    {...rest}
                    as={motion.a}
                    variants={linkVariants}
                    initial="rest"
                    whileHover="hover"
                >
                    {children}
                    <Underline variants={underlineVariants} transition={underlineTransition} />
                </StyledAnchor>
            </MotionWrap>
        );
    }
    return (
        <MotionWrap $block={false}>
            <StyledInlineSpan
                $block={false}
                $markdownInline={inheritMarkdownSize}
                variants={linkVariants}
                initial="rest"
                whileHover="hover"
                {...rest}
            >
                {children}
                <Underline variants={underlineVariants} transition={underlineTransition} />
            </StyledInlineSpan>
        </MotionWrap>
    );
}

/**
 * Link with primary color on hover and left-to-right underline animation (Framer Motion).
 * Underline is only as wide as the link text (link is inline-block).
 */
const Link = ({ to, href, children, block = false, ...rest }) => {
    if (href != null) {
        return (
            <MotionWrap $block={block}>
                <StyledAnchor
                    href={href}
                    $block={block}
                    {...rest}
                    as={motion.a}
                    variants={linkVariants}
                    initial="rest"
                    whileHover="hover"
                >
                    {children}
                    <Underline variants={underlineVariants} transition={underlineTransition} />
                </StyledAnchor>
            </MotionWrap>
        );
    }
    return (
        <MotionWrap $block={block}>
            <StyledRouterLink
                to={to}
                $block={block}
                {...rest}
                as={motion(RouterLink)}
                variants={linkVariants}
                initial="rest"
                whileHover="hover"
            >
                {children}
                <Underline variants={underlineVariants} transition={underlineTransition} />
            </StyledRouterLink>
        </MotionWrap>
    );
};

/** Button with same look as Link (primary color, underline animation). Use for actions e.g. Mark as done. */
export const LinkButton = ({ onClick, disabled, children, type = 'button', ...rest }) => (
    <StyledLinkButton type={type} onClick={onClick} disabled={disabled} {...rest}>
        {children}
    </StyledLinkButton>
);

export default Link;
